import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Kunde } from './kunde.service';

export interface Standort {
  id: string;
  nummer: number;
  name: string;
  status: number;
  kunde_id: string; // Direct relationship to client
  techniker_id: number; // Foreign key to mitarbeiter table (where is_techniker = true)
  bemerkung_struktur_anmerkung: string;
  zugriffsrechte: number;
  bericht_sprache: string;
  anmeldung_besuche: string;
  bericht_versand: string;
  besuchsbericht: number;
  mit_auswertung_tabelle: number;
  kontaktadressen: number;
  herkunft: string;
  anrede: string;
  bericht_email?: string; // E-Mail-Adresse für Berichtsversand
  
  // New date fields
  datum_ab_wann?: string;
  datum_bis?: string;
  
  // Address fields
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  
  // Timestamp fields
  created_at?: string;
  updated_at?: string;
  
  // Relationships (loaded via with())
  kunde?: Kunde; // Client object
}

export interface Kontakt {
  id: string;
  name: string;
  email: string;
}

// Account interface removed - not used in standort

export interface StandortResponse {
  result: number;
  data: Standort[];
  total?: number;
  page?: number;
  limit?: number;
  sec: string;
}

export interface StandortSingleResponse {
  result: number;
  data: Standort;
  sec: string;
}

@Injectable({
  providedIn: 'root'
})
export class StandortService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { 
    console.log('StandortService: Constructor called');
    console.log('StandortService: apiUrl set to:', this.apiUrl);
    console.log('StandortService: http client available:', !!this.http);
  }

  /**
   * Resolve PNG symbol URL for an indicator code using environment mapping
   */
  getIndicatorSymbolUrl(code?: number | null): string | null {
    if (code == null) return null;
    const key = (environment as any).indikatorSymbolMap?.[code];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.png`;
  }

  /**
   * Get all standorte with pagination
   */
  getStandorte(page: number = 1, limit: number = 10, search: string = '', sortBy: string = 'name', sortOrder: string = 'asc', kundeId?: string): Observable<StandortResponse> {
    const params: any = {
      page: page.toString(),
      limit: limit.toString(),
      search: search,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    if (kundeId) {
      params.kunde_id = kundeId;
    }
    
    console.log('StandortService: Making API call to', `${this.apiUrl}/standort`, 'with params:', params);
    return this.http.get<StandortResponse>(`${this.apiUrl}/standort`, { params });
  }

  /**
   * Get single standort by ID
   */
  getStandort(id: string): Observable<StandortSingleResponse> {
    return this.http.get<StandortSingleResponse>(`${this.apiUrl}/standort/${id}`);
  }

  /**
   * Create new standort
   */
  createStandort(standort: Partial<Standort>): Observable<StandortSingleResponse> {
    return this.http.post<StandortSingleResponse>(`${this.apiUrl}/standort`, standort);
  }

  /**
   * Update standort
   */
  updateStandort(id: string, standort: Partial<Standort>): Observable<StandortSingleResponse> {
    return this.http.put<StandortSingleResponse>(`${this.apiUrl}/standort/${id}`, standort);
  }

  /**
   * Delete standort
   */
  deleteStandort(id: string): Observable<StandortSingleResponse> {
    return this.http.delete<StandortSingleResponse>(`${this.apiUrl}/standort/${id}`);
  }

  /**
   * Get standorte by kunde ID
   */
  getStandorteByKundeId(kundeId: string, page: number = 1, limit: number = 10): Observable<StandortResponse> {
    console.log('StandortService: getStandorteByKundeId called with kundeId:', kundeId, 'page:', page, 'limit:', limit);
    return this.getStandorte(page, limit, '', 'name', 'asc', kundeId);
  }

  /**
   * Get all standort status for dropdown
   */
  getStandortStatus(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { id: 1, code: 'AKTIV', beschreibung: 'Aktiv' },
          { id: 2, code: 'INAKTIV', beschreibung: 'Inaktiv' },
          { id: 3, code: 'IN_BEARBEITUNG', beschreibung: 'In Bearbeitung' },
          { id: 4, code: 'GESPERRT', beschreibung: 'Gesperrt' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Get all niederlassung for dropdown
   */
  getNiederlassungen(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { code: 'WIEN', bezeichnung: 'Wien' },
          { code: 'GRAZ', bezeichnung: 'Graz' },
          { code: 'LINZ', bezeichnung: 'Linz' },
          { code: 'SALZBURG', bezeichnung: 'Salzburg' },
          { code: 'INNSBRUCK', bezeichnung: 'Innsbruck' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Get all kontakte for dropdown
   */
  getKontakte(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { id: '1', name: 'Max Mustermann', email: 'max@example.com' },
          { id: '2', name: 'Anna Schmidt', email: 'anna@example.com' },
          { id: '3', name: 'Peter Müller', email: 'peter@example.com' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Print single indicator QR label (HTML preview)
   */
  printQRIndicatorLabel(indicatorId: string): Observable<string> {
    const url = `${this.apiUrl}/pdf-qr-indicator/${indicatorId}?debug_html=1`;
    return this.http.get(url, {
      responseType: 'text'
    });
  }

  /**
   * Generate single indicator QR label PDF
   */
  generateQRIndicatorLabelPDF(indicatorId: string): Observable<Blob> {
    // Debug endpoints are in public section, not under /v5/
    const debugUrl = this.apiUrl.replace('/v5', '');
    const url = `${debugUrl}/debug-pdf-qr-indicator/${indicatorId}?pdf=true`;
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  /**
   * Print all indicators QR labels for a standort (HTML preview)
   */
  printQRIndicatorLabels(standortId: string): Observable<string> {
    const url = `${this.apiUrl}/pdf-qr-indicators/${standortId}?debug_html=1`;
    console.log('DEBUG: Calling URL:', url);
    return this.http.get(url, {
      responseType: 'text'
    });
  }

  /**
   * Generate all indicators QR labels PDF
   */
  generateQRIndicatorLabelsPDF(standortId: string): Observable<Blob> {
    // Debug endpoints are in public section, not under /v5/
    const debugUrl = this.apiUrl.replace('/v5', '');
    const url = `${debugUrl}/debug-pdf-qr-indicators/${standortId}?pdf=true`;
    console.log('DEBUG: Calling URL:', url);
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

}

